import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useFeedData, type FeedSortOption } from '@/hooks/useFeedData';
import { groupBySubcategory } from '@/lib/feedCategoryGrouping';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { T } from '@/i18n';
import './CategorySidebarLayout.css';

const Post = React.lazy(() => import('@/modules/posts/PostPage'));
const UserPage = React.lazy(() => import('@/modules/pages/UserPage'));

interface CategorySidebarLayoutProps {
  sortBy?: FeedSortOption;
  navigationSource?: 'home' | 'collection' | 'tag' | 'user' | 'search';
  navigationSourceId?: string;
  categorySlugs?: string[];
  categoryIds?: string[];
  contentType?: 'posts' | 'pages' | 'pictures' | 'files' | 'places';
  visibilityFilter?: 'invisible' | 'private';
  center?: boolean;
  preset?: any;
  /** Outer frame / stage border (default true). */
  showBorder?: boolean;
  /** Unconstrained height — grow with content instead of filling parent. */
  autoHeight?: boolean;
}

/** Compact nav row — title only (items are the sidebar, not categories). */
const NavItem = ({
  item,
  isSelected,
  onClick,
}: {
  item: any;
  isSelected: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    id={`list-item-${item.id}`}
    className="pm-cat-sidebar__item"
    aria-current={isSelected ? 'true' : undefined}
    onClick={onClick}
    title={item.title || 'Untitled'}
  >
    <span className="pm-cat-sidebar__label">{item.title || 'Untitled'}</span>
  </button>
);

/**
 * Desktop-only: feed items as sidebar nav (grouped into subcategory sections) + detail pane.
 * Categories are section headers only — not nav destinations.
 */
export const CategorySidebarLayout: React.FC<CategorySidebarLayoutProps> = ({
  sortBy = 'latest',
  navigationSource = 'home',
  navigationSourceId,
  categorySlugs,
  categoryIds,
  contentType,
  visibilityFilter,
  center,
  preset: _preset,
  showBorder = true,
  autoHeight = false,
}) => {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filterSlugs = useMemo(
    () => categorySlugs?.filter(Boolean) || [],
    [categorySlugs],
  );

  const {
    posts: feedPosts,
    loading,
    hasMore,
    loadMore,
  } = useFeedData({
    source: navigationSource,
    sourceId: navigationSourceId,
    sortBy,
    categorySlugs,
    categoryIds,
    contentType,
    visibilityFilter,
  });

  const sections = useMemo(
    () =>
      groupBySubcategory(
        feedPosts as any[],
        (post) => post.category_paths,
        filterSlugs,
      ),
    [feedPosts, filterSlugs],
  );

  const flatPosts = useMemo(
    () => sections.flatMap((s) => s.items),
    [sections],
  );

  // Reset selection when parent category filter changes
  useEffect(() => {
    setSelectedId(null);
  }, [categoryIds?.[0], categorySlugs?.join(',')]);

  // Auto-select first item
  useEffect(() => {
    if (!selectedId && flatPosts.length > 0) {
      setSelectedId((flatPosts[0] as any).id);
    } else if (selectedId && !flatPosts.some((p: any) => p.id === selectedId)) {
      setSelectedId(flatPosts.length > 0 ? (flatPosts[0] as any).id : null);
    }
  }, [flatPosts, selectedId]);

  // Keyboard navigation across flattened item list
  useEffect(() => {
    if (!selectedId) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      e.preventDefault();
      const currentIndex = flatPosts.findIndex((p: any) => p.id === selectedId);
      if (currentIndex === -1) return;

      let newIndex = currentIndex;
      if (e.key === 'ArrowDown' && currentIndex < flatPosts.length - 1) {
        newIndex = currentIndex + 1;
      } else if (e.key === 'ArrowUp' && currentIndex > 0) {
        newIndex = currentIndex - 1;
      }

      if (newIndex !== currentIndex) {
        setSelectedId((flatPosts[newIndex] as any).id);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedId, flatPosts]);

  const handleItemClick = (item: any) => {
    if (item.meta?.url && item.type === 'page-external') {
      navigate(item.meta.url);
      return;
    }
    setSelectedId(item.id);
  };

  const renderDetail = () => {
    if (!selectedId) {
      return (
        <div className="h-full flex items-center justify-center text-muted-foreground">
          <T>Select an item to view details</T>
        </div>
      );
    }

    const selectedPost = flatPosts.find((p: any) => p.id === selectedId) as any;
    if (!selectedPost) {
      return (
        <div className="h-full flex items-center justify-center text-muted-foreground">
          <T>Select an item to view details</T>
        </div>
      );
    }

    const slug =
      selectedPost.meta?.slug ||
      selectedPost.cover?.meta?.slug ||
      selectedPost.pictures?.[0]?.meta?.slug;

    if (selectedPost?.type === 'page-intern' && slug) {
      return (
        <React.Suspense
          fallback={
            <div className="h-full flex items-center justify-center text-muted-foreground">
              Loading...
            </div>
          }
        >
          <UserPage
            userId={selectedPost.user_id}
            slug={slug}
            embedded
          />
        </React.Suspense>
      );
    }

    if (selectedPost?.type === 'place-search' && selectedPost.meta?.url) {
      return (
        <div className="h-full overflow-y-auto p-6 flex flex-col gap-4">
          <h2 className="text-xl font-semibold">{selectedPost.title}</h2>
          {selectedPost.description && (
            <p className="text-muted-foreground text-sm">{selectedPost.description}</p>
          )}
          <Link to={selectedPost.meta.url} className="text-primary font-medium hover:underline w-fit">
            <T>Open place details</T>
          </Link>
        </div>
      );
    }

    return (
      <React.Suspense
        fallback={
          <div className="h-full flex items-center justify-center text-muted-foreground">
            Loading...
          </div>
        }
      >
        <Post
          key={selectedId}
          postId={selectedId}
          embedded
          className="h-full min-h-0 overflow-hidden flex flex-col"
        />
      </React.Suspense>
    );
  };

  return (
    <div
      className={cn(
        'pm-cat-sidebar',
        showBorder && 'pm-cat-sidebar--bordered',
        autoHeight && 'pm-cat-sidebar--auto',
        center && 'max-w-7xl mx-auto',
      )}
    >
      <nav className="pm-cat-sidebar__nav" aria-label="Feed items">
        <div className="pm-cat-sidebar__scroll">
          {loading && flatPosts.length === 0 ? (
            <div className="pm-cat-sidebar__empty">Loading...</div>
          ) : flatPosts.length === 0 ? (
            <div className="pm-cat-sidebar__empty">
              <T>No posts found.</T>
            </div>
          ) : (
            sections.map((section) => (
              <div key={section.key} className="pm-cat-sidebar__section">
                {section.title && (
                  <div className="pm-cat-sidebar__sec">
                    <T>{section.title}</T>
                  </div>
                )}
                {section.items.map((post: any) => (
                  <NavItem
                    key={post.id}
                    item={post}
                    isSelected={selectedId === post.id}
                    onClick={() => handleItemClick(post)}
                  />
                ))}
              </div>
            ))
          )}
          {hasMore && (
            <div className="pm-cat-sidebar__more">
              <Button variant="ghost" size="sm" onClick={() => loadMore()}>
                <T>Load More</T>
              </Button>
            </div>
          )}
        </div>
      </nav>

      <div className="pm-cat-sidebar__stage">
        <div className="pm-cat-sidebar__stage-scroll">{renderDetail()}</div>
      </div>
    </div>
  );
};

export default CategorySidebarLayout;
